import { useMemo } from 'react';
import { useJpa, useRequest } from '@stores';
import { connect } from 'redux-bundler-react';

import JDFormInstructions from '@forms/static-forms/JDFormInstructions';
import Contacts from '@forms/input-forms/common/Contacts';
import SupportingInformation from '@forms/input-forms/common/SupportingInformation';
import JDFormCertifySignSubmit from '@forms/input-forms/JDForm/JDFormCertifySignSubmit';
import JDFormPropertyOwners from '@forms/input-forms/JDForm/JDFormPropertyOwners';
import { JDFormAquaticResources } from '@forms/input-forms/common/AquaticResources';
import JpaInfo from './JpaInfo';
import JpaForm, { generateJpaFormName } from './JpaForm';
import GeneralProjectInformation from '@forms/input-forms/common/GeneralProjectInformation';
import JDFormInformation from '@forms/input-forms/JDForm/JDFormInformation';

//List of steps for vertical stepper, these can also be used for form header. index = step ID
export const getJDForm1aMetadata = () => {
  const baseFormArray = [
    JDFormInstructions.metadata,
    Contacts.metadata,
    GeneralProjectInformation.metadata,
    { sectionName: 'Delineation Information' },
    JDFormPropertyOwners.metadata,
    JDFormAquaticResources.metadata,
    SupportingInformation.metadata,
    JDFormCertifySignSubmit.metadata,
  ];
  const jpaBasicInfo = useJpa.getState().jpaBasicInfo;
  const isJpa = jpaBasicInfo?.length > 0;

  // insert JPA sidebar steps
  if (isJpa) {
    const jpaInfoSteps = [
      {
        sectionName: 'Location Specific Information',
        isParentStep: true,
      },
    ];
    const jpaFormSteps = [
      {
        sectionName: 'Location Specific Questions',
        isParentStep: true,
      },
    ];

    jpaBasicInfo.forEach((jpa) => {
      jpaInfoSteps.push({
        sectionName: generateJpaFormName(jpa),
        isSubStep: true,
        parentId: 1,
      });
      jpaFormSteps.push({
        sectionName: generateJpaFormName(jpa),
        isSubStep: true,
        parentId: jpaBasicInfo?.length + baseFormArray.length - 1,
      });
    });

    baseFormArray.splice(1, 0, ...jpaInfoSteps);
    baseFormArray.splice(baseFormArray.length - 2, 0, ...jpaFormSteps);
  }

  return baseFormArray;
};

const generateSections = ({ doUpdateJDRequest, isJpa, jpaBasicInfo, jDData, jDRequestID }) => {
  const sections = [
    {
      id: 'JD1aInstructions',
      name: 'Instructions',
      content: JDFormInstructions,
    },
    {
      id: 'JD1aContactInformation',
      name: 'Contact Information',
      content: Contacts,
      subtext: 'submitting the delineation report and/or requesting the jurisdictional determination.',
      componentID: jDRequestID,
    },
    {
      id: 'GeneralProjectInformation',
      name: 'General Project Information',
      content: GeneralProjectInformation,
    },
    {
      id: 'JD1aInformation',
      name: 'Delineation Information',
      content: JDFormInformation,
    },
    {
      id: 'JD1aPropertyOwners',
      name: 'Property Owners',
      content: JDFormPropertyOwners,
      componentID: jDRequestID,
    },
    {
      id: 'JD1aAquaticResources',
      name: 'Aquatic Resources',
      content: JDFormAquaticResources,
    },
    {
      id: 'JD1aSupportingInformation',
      name: 'Supporting Information',
      content: SupportingInformation,
      componentID: jDRequestID,
      subtext: (
        <>
          <p>
            To facilitate your request, we strongly encourage a copy of the Delineation Report and supporting
            information be uploaded in this section. The types of supporting information that should be provided are
            contained in the drop-down list when you click on the "Add a Document" tab. Information that is most helpful
            in the USACE review are aerial photographs, automated wetland data sheets, and maps/plans/plots. Multiple
            documents of the same file type can be combined and uploaded as one document.
          </p>
        </>
      ),
      updateRequestData: doUpdateJDRequest,
    },
    {
      id: 'JD1aCertifySignSubmit',
      name: 'Certify Sign Submit',
      content: JDFormCertifySignSubmit,
    },
  ];

  // insert JPA content
  if (isJpa) {
    const jpaInfoSections = [
      {
        id: 'jpa-info',
        name: 'Location Specific Information',
        content: () => <></>,
      },
    ];
    const jpaFormSections = [
      {
        id: 'jpa-questions',
        name: 'Location Specific Information',
        content: () => <></>,
      },
    ];

    const infoStartIndex = 1;
    const formStartIndex = sections.length + jpaBasicInfo.length - 1;
    const infoGroupIndexes = [];
    const formGroupIndexes = [];

    jpaBasicInfo.forEach((_, jpaIndex) => {
      infoGroupIndexes.push(jpaIndex + infoStartIndex + 1);
      formGroupIndexes.push(jpaIndex + formStartIndex + 1);
    });

    jpaBasicInfo.forEach((jpa) => {
      jpaInfoSections.push({
        id: `jpa-info-${jpa.formID}`,
        name: generateJpaFormName(jpa),
        content: JpaInfo,
        componentId: jDRequestID,
        formId: jpa.formID,
        group: {
          indexes: infoGroupIndexes,
          parentIndex: infoStartIndex,
          parentName: jpaInfoSections[0].name,
        },
        requestId: jDData.requestID,
        version: jDData.version,
      });
      jpaFormSections.push({
        id: `jpa-questions-${jpa.formID}`,
        name: generateJpaFormName(jpa),
        content: JpaForm,
        componentId: jDRequestID,
        formId: jpa.formID,
        group: {
          indexes: formGroupIndexes,
          parentIndex: formStartIndex,
          parentName: jpaFormSections[0].name,
        },
        requestId: jDData.requestID,
        version: jDData.version,
      });
    });

    sections.splice(1, 0, ...jpaInfoSections);
    sections.splice(sections.length - 2, 0, ...jpaFormSections);
  }

  return sections;
};

const JDForm1a = connect(
  'doUpdateJDRequest',
  'selectJDRequestID',
  'selectJDData',
  ({ doUpdateJDRequest, jDRequestID, jDData }) => {
    const { getActiveSection } = useRequest();
    const { jpaBasicInfo } = useJpa();
    const isJpa = jpaBasicInfo?.length > 0;
    const activeStep = getActiveSection();

    const sections = useMemo(
      () => generateSections({ doUpdateJDRequest, isJpa, jpaBasicInfo, jDData, jDRequestID }),
      [doUpdateJDRequest, isJpa, jpaBasicInfo, jDData, jDRequestID]
    );

    return sections.map((section, index) => {
      const { content: Content, ...props } = section;

      return (
        <section
          id={section.id}
          key={section.id}
          name={section.name}
          style={{ display: activeStep?.id === index ? 'inline' : 'none' }}
        >
          <Content stepNo={index} {...props} {...section.props} />
        </section>
      );
    });
  }
);

export default JDForm1a;
